import { Component, inject, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  ReviewSessionExtended,
  ReviewInsight,
  PersonaInfo,
  BackendReviewSession,
  ReviewDataTransformer
} from 'src/app/models/review.interface';
import { Persona } from 'src/app/models/personas.interface';
import { ReviewService } from 'src/app/services/review.service';
import { PersonaService } from 'src/app/services/persona.service';
import { Strings } from 'src/app/shared/strings';

@Component({
  selector: 'app-reviewers-details',
  templateUrl: './reviewers.details.component.html',
  styleUrls: ['./reviewers.details.component.scss']
})
export class ReviewersDetailsComponent implements OnInit {
  currentDate = new Date().toLocaleDateString();
  cols = 3;
  strings = Strings;
  reviewerSession!: ReviewSessionExtended;
  _snackBar = inject(MatSnackBar);
  personas: Persona[] = [];
  personaMap = new Map<string, Persona>();
  isExporting = false;
  hideDuplicates = false;

  constructor(
    private router: Router,
    private reviewerService: ReviewService,
    private personaService: PersonaService
  ) {}

  async ngOnInit() {
    const reviewer_id = this.router.url.split('/').pop();
    if (!reviewer_id) {
      console.error(`Invalid reviewer ID ${reviewer_id} provided`);
      this.router.navigate(['/reviewers']);
      return;
    }

    try {
      // Load personas first to ensure consistent name resolution
      await this.loadPersonas();

      // Fetch review session from API
      const backendSession = (await firstValueFrom(
        this.reviewerService.getReviewSession(reviewer_id)
      )) as BackendReviewSession;

      // Transform backend response to frontend interface with persona map
      this.reviewerSession = ReviewDataTransformer.transformReviewSession(backendSession, this.personaMap);

      // Verify we have findings data
      if (!this.hasFindings() || this.getFindings().length === 0) {
        console.warn('No findings found in review session');
        this._snackBar.open('This review has no data yet. Please wait for processing to complete.', 'Close', {
          duration: 5000
        });
        this.router.navigate(['/reviewers']);
        return;
      }

      console.log('Loaded review with', this.reviewerSession.findings.length, 'findings');
      console.log('Personas:', this.reviewerSession.personas);
      console.log('findings:', this.reviewerSession.findings);
    } catch (error) {
      console.error('Error loading review session:', error);
      this._snackBar.open('Failed to load review data. Please try again.', 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
      this.router.navigate(['/reviewers']);
    }
  }

  /**
   * Load personas from PersonaService and populate persona map for efficient lookups.
   * This ensures consistent persona name resolution across all findings.
   */
  async loadPersonas(): Promise<void> {
    try {
      this.personas = await firstValueFrom(this.personaService.getPersonas());

      // Populate persona map for O(1) lookups during transformation
      this.personaMap.clear();
      this.personas.forEach(persona => {
        if (persona.id) {
          this.personaMap.set(persona.id, persona);
        }
      });

      console.log(`Loaded ${this.personas.length} personas for name resolution`);
    } catch (error) {
      console.error('Failed to load personas:', error);
      this._snackBar.open('Failed to load reviewer information. Persona names may not display correctly.', 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
      // Continue with review loading - transformer will use fallback names
    }
  }

  showView() {
    this.router.navigate(['/reviewers']);
  }

  toClipboard(text: string) {
    navigator.clipboard.writeText(text);
    this._snackBar.open('Copied to clipboard', 'Close', {
      duration: 2000
    });
  }

  // TODO: Implement saveNotes?
  // saveNotes() {
  //   // Save to backend
  //   this.reviewerService.updateReviewSession(this.reviewerSession).subscribe({
  //     next: () => {
  //       this._snackBar.open('Notes saved successfully', 'Close', {
  //         duration: 3000,
  //         panelClass: ['success-snackbar']
  //       });
  //     },
  //     error: error => {
  //       console.error('Error saving notes:', error);
  //       this._snackBar.open('Error saving notes', 'Close', {
  //         duration: 9000,
  //         panelClass: ['error-snackbar']
  //       });
  //     }
  //   });
  // }

  getUnresolvedCount(): number {
    return this.getFilteredFindings().filter(finding => finding.resolved === false).length;
  }

  getResolvedCount(): number {
    return this.getFilteredFindings().filter(finding => finding.resolved === true).length;
  }

  getTotalCount(): number {
    return this.getFilteredFindings().length;
  }

  /**
   * Get formatted location path from insight
   * Concatenates section > subsection, filtering out empty values and duplicates
   * Falls back to finding field if both section and subsection are empty
   */
  getLocationPath(insight: ReviewInsight): string {
    const section = insight.section?.trim() || '';
    const subsection = insight.subsection?.trim() || '';

    // If both are empty, fall back to finding field
    if (!section && !subsection) {
      return insight.finding || 'No location specified';
    }

    // If only one exists, return it
    if (!section) return subsection;
    if (!subsection) return section;

    // Check if subsection is a duplicate or contains the section
    // This handles cases where LLM puts same text in both fields
    if (section.toLowerCase() === subsection.toLowerCase()) {
      return section;
    }

    // Check if subsection starts with or contains the section text
    if (subsection.toLowerCase().includes(section.toLowerCase())) {
      return subsection;
    }

    // Both are different and valid, show section > subsection
    return `${section} > ${subsection}`;
  }

  /**
   * Get persona names as comma-separated string
   */
  getPersonaNames(): string {
    return this.reviewerSession.personas.map((p: PersonaInfo) => p.name).join(', ');
  }

  /**
   * Check if current session has findings (for template use)
   */
  hasFindings(): boolean {
    return !!this.reviewerSession?.findings;
  }

  /**
   * Get findings array (for template use)
   */
  getFindings(): ReviewInsight[] {
    return this.reviewerSession?.findings || [];
  }

  /**
   * Check if a finding is a duplicate
   * A finding is considered a duplicate if it has a non-empty duplicate field
   */
  isDuplicate(finding: ReviewInsight): boolean {
    return !!finding?.duplicate && typeof finding.duplicate === 'string' && finding.duplicate.trim() !== '';
  }

  /**
   * Get filtered findings based on toggle state
   * When hideDuplicates is true, excludes findings with non-empty duplicate field
   */
  getFilteredFindings(): ReviewInsight[] {
    const findings = this.getFindings();
    if (!this.hideDuplicates) {
      return findings;
    }
    return findings.filter(finding => !this.isDuplicate(finding));
  }

  /**
   * Toggle finding resolved status
   * Implements optimistic update with API integration
   */
  toggleFinding(insightId: string) {
    // Find the finding by insight_id in the original findings array
    const findingIdx = this.reviewerSession.findings.findIndex(f => f.insight_id === insightId);

    if (findingIdx === -1) {
      console.error('Finding not found with insight_id:', insightId);
      return;
    }

    const finding = this.reviewerSession.findings[findingIdx];
    const resolvedStatus = !finding.resolved;
    const previousStatus = finding.resolved;
    const issueId = `${findingIdx + 1}`;

    // Optimistic update - update UI immediately
    finding.resolved = resolvedStatus;

    // Call API to persist the change
    this.reviewerService
      .updateFindingStatus(this.reviewerSession.review_id, finding.insight_id, resolvedStatus)
      .subscribe({
        next: () => {
          // Success - show confirmation message
          this._snackBar.open(`Issue ${issueId} has been ${resolvedStatus ? 'resolved' : 'unresolved'}`, 'Close', {
            duration: 3000,
            panelClass: ['success-snackbar']
          });
        },
        error: error => {
          // Error - revert the optimistic update and show error message
          console.error('Error updating finding status:', error);
          finding.resolved = previousStatus;

          this._snackBar.open(`Failed to update Issue ${issueId}. Please try again.`, 'Close', {
            duration: 5000,
            panelClass: ['error-snackbar']
          });
        }
      });
  }

  /**
   * Get CSS class for severity indicator based on severity level
   */
  getSeverityClass(severity: 'High' | 'Medium' | 'Low'): string {
    switch (severity) {
      case 'High':
        return 'severity--high';
      case 'Medium':
        return 'severity--medium';
      case 'Low':
        return 'severity--low';
      default:
        return 'severity--low';
    }
  }

  /**
   * Get accessible label for severity indicator
   */
  getSeverityLabel(severity: 'High' | 'Medium' | 'Low'): string {
    return `${severity} severity finding`;
  }

  /**
   * Export review findings to Excel
   * Shows loading indicator, calls API, triggers download, and handles errors
   */
  exportToExcel(): void {
    if (!this.reviewerSession?.review_id) {
      this._snackBar.open('Unable to export: Review ID not found', 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    // Set loading state to disable button and show loading indicator
    this.isExporting = true;

    // Call service to export review
    this.reviewerService.exportReviewToExcel(this.reviewerSession.review_id).subscribe({
      next: response => {
        // Clear loading state
        this.isExporting = false;

        // Trigger file download
        this.downloadFile(response.blob, response.filename);

        // Show success message
        this._snackBar.open('Excel file downloaded successfully', 'Close', {
          duration: 3000,
          panelClass: ['success-snackbar']
        });
      },
      error: error => {
        // Clear loading state
        this.isExporting = false;

        // Handle error with specific messages and retry option
        this.handleExportError(error);
      }
    });
  }

  /**
   * Handle export errors with specific error messages and retry option
   * Displays appropriate error message based on error type and provides retry action
   */
  private handleExportError(error: { status?: number; message?: string }): void {
    // Determine error message and whether retry is appropriate
    let errorMessage = 'Failed to export review. Please try again.';
    let showRetry = true;

    if (error.status === 404) {
      errorMessage = 'Review not found. Please refresh and try again.';
      showRetry = false; // No point retrying if review doesn't exist
    } else if (error.status === 400) {
      errorMessage = 'No findings available for export.';
      showRetry = false; // No point retrying if there are no findings
    } else if (error.status === 401 || error.status === 403) {
      errorMessage = 'You do not have permission to export this review.';
      showRetry = false; // No point retrying if unauthorized
    } else if (error.status === 0) {
      errorMessage = 'Network error. Please check your connection and try again.';
      showRetry = true; // Network errors are often transient
    } else if (error.status === 500) {
      errorMessage = 'Server error occurred. Please try again.';
      showRetry = true; // Server errors might be transient
    } else if (error.status === 504 || error.status === 408) {
      errorMessage = 'Request timed out. Please try again.';
      showRetry = true; // Timeout errors are often transient
    }

    console.error('Error exporting review:', error);

    // Show error message with retry option if appropriate
    const snackBarRef = this._snackBar.open(errorMessage, showRetry ? 'Retry' : 'Close', {
      duration: showRetry ? 8000 : 5000, // Longer duration if retry is available
      panelClass: ['error-snackbar']
    });

    // Handle retry action
    if (showRetry) {
      snackBarRef.onAction().subscribe(() => {
        console.log('Retrying export...');
        this.exportToExcel();
      });
    }
  }

  /**
   * Download file from Blob
   * Creates temporary anchor element to trigger browser download
   */
  private downloadFile(blob: Blob, filename: string): void {
    try {
      // Create object URL from Blob
      const url = window.URL.createObjectURL(blob);

      // Create temporary anchor element
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.style.display = 'none';

      // Append to body, click, and remove
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      // Clean up object URL after a short delay to ensure download starts
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Error downloading file:', error);
      this._snackBar.open('Failed to download file. Please try again.', 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
    }
  }
}
